import { doc, getDoc, updateDoc, serverTimestamp, runTransaction, deleteField, type UpdateData } from 'firebase/firestore';
import { db } from '../firebase';

export type ConnectionPhase = 'chat' | 'call' | 'video' | 'physical' | 'completed';
export type ConnectionPhaseStatus = 'not_scheduled' | 'scheduled' | 'ready' | 'in_progress' | 'done';

/**
 * Deterministic transition map — the single source of truth for what phase can
 * follow what.  Validated at write-time inside `transitionPhaseGuarded`.
 */
export const ALLOWED_TRANSITIONS: Record<ConnectionPhase, ConnectionPhase[]> = {
  chat: ['call'],
  call: ['video'],
  video: ['physical'],
  physical: ['completed'], // physical → completed: the flow is fully done
  completed: [],           // terminal state — no further transitions
};

export type TransitionResult =
  | { success: true; nextPhase: ConnectionPhase }
  | { success: false; reason: string; actualPhase?: ConnectionPhase };

const defaults = {
  phase: 'chat' as ConnectionPhase,
  phaseStatus: 'not_scheduled' as ConnectionPhaseStatus,
  activeEventId: null as string | null
};

export async function ensureConnectionJourneyFields(connectionId: string) {
  if (!connectionId) return;
  const ref = doc(db, 'connections', connectionId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const data = snap.data();

  const update: Partial<typeof defaults> = {};
  if (data.phase === undefined) update.phase = defaults.phase;
  if (data.phaseStatus === undefined) update.phaseStatus = defaults.phaseStatus;
  if (data.activeEventId === undefined) update.activeEventId = defaults.activeEventId;

  if (Object.keys(update).length > 0) {
    await updateDoc(ref, update);
  }
}

export async function setConnectionPhase(
  connectionId: string,
  phase: ConnectionPhase,
  phaseStatus: ConnectionPhaseStatus = 'not_scheduled'
) {
  if (!connectionId) return;
  const ref = doc(db, 'connections', connectionId);
  await updateDoc(ref, { phase, phaseStatus });
}

export async function setConnectionPhaseStatus(
  connectionId: string,
  phaseStatus: ConnectionPhaseStatus,
  activeEventId?: string | null
) {
  if (!connectionId) return;
  const ref = doc(db, 'connections', connectionId);
  const payload: any = { phaseStatus };
  if (activeEventId !== undefined) {
    payload.activeEventId = activeEventId;
  }
  await updateDoc(ref, payload);
}

/**
 * Atomically transition the connection phase using a Firestore transaction.
 *
 * Guards against:
 *  - Phase skipping  (only ALLOWED_TRANSITIONS are accepted)
 *  - Double writes   (validates that Firestore still holds the expected phase)
 *  - Race conditions (two users writing simultaneously; last valid write wins)
 *
 * Returns success=true (with idempotent no-op) when the phase is already at
 * `nextPhase`, so callers don't need to check first.
 */
export async function transitionPhaseGuarded(
  connectionId: string,
  expectedCurrentPhase: ConnectionPhase,
  nextPhase: ConnectionPhase,
  extraFields: Record<string, unknown> = {}
): Promise<TransitionResult> {
  if (!connectionId) return { success: false, reason: 'missing connectionId' };

  const allowed = ALLOWED_TRANSITIONS[expectedCurrentPhase] ?? [];
  if (!allowed.includes(nextPhase)) {
    return {
      success: false,
      reason: `Transition ${expectedCurrentPhase} → ${nextPhase} is not allowed`,
    };
  }

  const ref = doc(db, 'connections', connectionId);
  try {
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(ref);
      if (!snap.exists()) throw Object.assign(new Error('not-found'), {});

      const actualPhase = ((snap.data().phase) ?? 'chat') as ConnectionPhase;

      // Idempotent: phase already at target — no-op, still a success
      if (actualPhase === nextPhase) return;

      // Another user already changed the phase to something unexpected
      if (actualPhase !== expectedCurrentPhase) {
        throw Object.assign(new Error('phase-mismatch'), { actualPhase });
      }

      transaction.update(ref, {
        phase: nextPhase,
        phaseStatus: 'not_scheduled',
        ...extraFields,
      });
    });

    if (process.env.NODE_ENV === 'development') {
      console.log(`[phase-guard] ✅ ${expectedCurrentPhase} → ${nextPhase} (${connectionId.slice(0, 6)})`);
    }
    return { success: true, nextPhase };
  } catch (err: any) {
    if (err.message === 'phase-mismatch') {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[phase-guard] ⚠️ mismatch: expected ${expectedCurrentPhase}, got ${err.actualPhase}`);
      }
      return {
        success: false,
        reason: `Phase mismatch: expected ${expectedCurrentPhase}, got ${err.actualPhase}`,
        actualPhase: err.actualPhase,
      };
    }
    if (err.message === 'not-found') {
      return { success: false, reason: 'Connection not found' };
    }
    throw err;
  }
}

export async function linkActiveEvent(
  connectionId: string,
  activeEventId: string | null,
  phase?: ConnectionPhase,
  phaseStatus: ConnectionPhaseStatus = 'scheduled'
) {
  if (!connectionId) return;
  const ref = doc(db, 'connections', connectionId);
  const payload: any = { activeEventId, phaseStatus };
  if (phase) payload.phase = phase;
  await updateDoc(ref, payload);
}

/**
 * Complete the current phase and atomically advance to the next phase.
 *
 * Uses a Firestore transaction so two concurrent users cannot both complete
 * the same phase, preventing double-writes and race conditions.
 *
 * @returns The next phase that was set, or null if no transition occurred.
 */
export async function completeCurrentPhase(connectionId: string): Promise<ConnectionPhase | null> {
  if (!connectionId) return null;
  const ref = doc(db, 'connections', connectionId);

  // Capture side-effect data outside the transaction so we can act after commit
  let capturedActiveEventId: string | null = null;

  const nextPhaseMap: Record<ConnectionPhase, ConnectionPhase | null> = {
    chat: null,          // chat completion is handled by handleDirectCall path
    call: 'video',
    video: 'physical',
    physical: 'completed', // physical → completed: both users are now fully connected
    completed: null,       // terminal — no further progression
  };

  let resolvedNextPhase: ConnectionPhase | null = null;

  try {
    resolvedNextPhase = await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(ref);
      if (!snap.exists()) return null;

      const data = snap.data() || {};
      const currentPhase: ConnectionPhase = data.phase ?? 'chat';
      const currentStatus: ConnectionPhaseStatus = data.phaseStatus ?? 'not_scheduled';
      capturedActiveEventId = data.activeEventId ?? null;

      const nextPhase = nextPhaseMap[currentPhase];

      // Guard: no progression defined, or already at same phase, or redundant done
      if (!nextPhase) return null;
      if (currentPhase === nextPhase) return null;
      if (currentStatus === 'done' && !capturedActiveEventId) return null;

      // Atomic: advance phase + clear active event in a single write
      const update: UpdateData<Record<string, unknown>> = {
        phase: nextPhase,
        phaseStatus: 'not_scheduled',
        activeEventId: deleteField(),
      };
      // When the physical cycle completes, mark connection as active again
      if (currentPhase === 'physical') {
        update.status = 'active';
      }
      transaction.update(ref, update);

      return nextPhase;
    });
  } catch (err) {
    console.error('[completeCurrentPhase] transaction failed', err);
    return null;
  }

  if (!resolvedNextPhase) return null;

  // Side effect: mark linked calendar event as completed (outside transaction — acceptable)
  if (capturedActiveEventId) {
    try {
      const eventRef = doc(db, 'calendarEvents', capturedActiveEventId);
      const eventSnap = await getDoc(eventRef);
      if (eventSnap.exists()) {
        const eventData = eventSnap.data() || {};
        const isMismatch = eventData.connectionId && eventData.connectionId !== connectionId;
        const isCancelled = eventData.status === 'cancelled';
        const alreadyCompleted = eventData.status === 'completed';
        if (!isMismatch && !isCancelled && !alreadyCompleted) {
          await updateDoc(eventRef, {
            status: 'completed',
            updatedAt: serverTimestamp(),
            completedAt: serverTimestamp()
          });
        }
      }
    } catch (eventErr) {
      // Non-critical — don't fail the whole phase completion if calendar update fails
      console.warn('[completeCurrentPhase] calendar event update failed', eventErr);
    }
  }

  if (process.env.NODE_ENV === 'development') {
    console.log(`[completeCurrentPhase] ✅ advanced to ${resolvedNextPhase} (${connectionId.slice(0, 6)})`);
  }

  return resolvedNextPhase;
}

/**
 * Atomically cancel the active appointment linked to a connection.
 *
 * Both writes (marking the calendarEvent cancelled AND clearing the
 * connection's activeEventId) run inside a single Firestore transaction so
 * the system can never end up in a half-cancelled state where one side has
 * been updated but the other has not.
 */
export async function cancelActiveAppointment(connectionId: string) {
  if (!connectionId) return;

  const connRef = doc(db, 'connections', connectionId);

  await runTransaction(db, async (transaction) => {
    const connSnap = await transaction.get(connRef);
    if (!connSnap.exists()) return;

    const conn = connSnap.data() || {};
    const activeEventId: string | null = conn.activeEventId ?? null;
    if (!activeEventId) return;

    const eventRef = doc(db, 'calendarEvents', activeEventId);
    const eventSnap = await transaction.get(eventRef);
    if (!eventSnap.exists()) {
      // Event already deleted — just clear the dangling reference on the connection
      transaction.update(connRef, { activeEventId: null, phaseStatus: 'not_scheduled' });
      return;
    }

    const eventData = eventSnap.data() || {};
    if (eventData.connectionId && eventData.connectionId !== connectionId) {
      // In development: include IDs for easier debugging.
      // In production: keep the error generic to avoid leaking document IDs in logs.
      if (process.env.NODE_ENV === 'development') {
        throw Object.assign(new Error('MISMATCH_EVENT'), { eventId: activeEventId, connectionId });
      }
      throw new Error('MISMATCH_EVENT');
    }

    // Atomic: cancel event + clear connection reference in one commit
    transaction.update(eventRef, { status: 'cancelled', updatedAt: serverTimestamp() });
    transaction.update(connRef, { activeEventId: null, phaseStatus: 'not_scheduled' });
  });
}

// Shared planning lock for connection-level planning state
export async function setPlanningLock(connectionId: string, lock: {
  status: 'idle' | 'planning' | 'scheduled' | 'proposal_sent' | 'cancelled';
  lockedByUid?: string | null;
  lockedByUsername?: string | null;
  lockReason?: 'later_clicked' | 'autoplan' | 'manual_plan' | string;
  expiresAt?: any;
}) {
  if (!connectionId) return;
  const ref = doc(db, 'connections', connectionId);
  await updateDoc(ref, {
    planningLock: {
      ...lock,
      lockedAt: serverTimestamp(),
    },
  });
}

// Planning lock acquisition result
export interface PlanningLockResult {
  granted: boolean;
  ownerId: string | null;
  ownerName: string | null;
  phase: ConnectionPhase | null;
  expiresAt: any;
}

// Transaction-based planning lock acquisition
// Returns { granted: true } if lock acquired, { granted: false } if someone else has lock
//
// Per-connection in-flight guards: using Maps instead of module-level booleans so
// simultaneous lock requests for DIFFERENT connections don't block each other.
const acquireLockInFlight = new Map<string, boolean>();

export async function acquirePlanningLock(
  connectionId: string,
  userId: string,
  userName: string,
  phase: ConnectionPhase
): Promise<PlanningLockResult> {
  if (!connectionId || !userId || !userName) {
    console.warn('[planning-lock] acquirePlanningLock called with invalid params');
    return { granted: false, ownerId: null, ownerName: null, phase: null, expiresAt: null };
  }

  // Per-connection single-flight guard
  if (acquireLockInFlight.get(connectionId)) {
    console.warn('[planning-lock] acquire already in flight for connection', connectionId);
    return { granted: false, ownerId: null, ownerName: null, phase: null, expiresAt: null };
  }

  try {
    acquireLockInFlight.set(connectionId, true);

    const ref = doc(db, 'connections', connectionId);

    // NOTE on client-side time: `Date.now()` reflects the local device clock, which
    // can drift or be set incorrectly. This means:
    //   - A client with a clock *ahead* may expire locks before the server considers them expired.
    //   - A client with a clock *behind* may hold a lock longer than intended.
    // The practical risk is low (10-minute expiry window) and the use case (planning
    // a meeting) is tolerant of a few minutes' drift.
    // A fully server-authoritative approach would require a Cloud Function or
    // Firestore security rule that validates timestamps server-side.
    const now = Date.now();
    const expiresAt = now + (10 * 60 * 1000); // 10 minutes from now

    const result = await runTransaction(db, async (transaction) => {
      const connSnap = await transaction.get(ref);
      
      if (!connSnap.exists()) {
        throw new Error('[planning-lock] connection not found');
      }

      const data = connSnap.data() || {};
      const currentLock = data.planningOwnerId;
      const currentExpiry = data.planningLockExpiresAt;

      // Check if lock is available (null, expired, or already held by this user)
      const isExpired = currentExpiry && currentExpiry < now;
      const isOwnLock = currentLock === userId;
      const isAvailable = !currentLock || isExpired || isOwnLock;

      if (isAvailable) {
        // Acquire or re-acquire lock — always update planningOwnerStartedAt so
        // the other user's PlanningListener detects a new planning session even
        // when the same user re-opens the agenda
        transaction.update(ref, {
          planningOwnerId: userId,
          planningOwnerName: userName,
          planningOwnerPhase: phase,
          planningOwnerStartedAt: serverTimestamp(),
          planningLockExpiresAt: expiresAt,
        });

        // Truncate IDs/names in production logs to avoid leaking sensitive data
        const logId = (s: string) => process.env.NODE_ENV === 'development' ? s : `${s.slice(0, 6)}…`;
        console.log('[planning-lock] acquire SUCCESS', {
          connectionId: logId(connectionId),
          userId: logId(userId),
          userName: process.env.NODE_ENV === 'development' ? userName : '***',
          phase,
          wasReacquire: isOwnLock,
        });

        return {
          granted: true,
          ownerId: userId,
          ownerName: userName,
          phase,
          expiresAt,
        };
      } else {
        // Lock held by someone else (and not expired)
        const logId = (s: string) => process.env.NODE_ENV === 'development' ? s : `${s.slice(0, 6)}…`;
        console.log('[planning-lock] acquire DENIED', {
          connectionId: logId(connectionId),
          currentOwner: process.env.NODE_ENV === 'development' ? data.planningOwnerName : '***',
          currentOwnerId: logId(currentLock || ''),
        });

        return {
          granted: false,
          ownerId: currentLock,
          ownerName: data.planningOwnerName || null,
          phase: data.planningOwnerPhase || null,
          expiresAt: currentExpiry || null,
        };
      }
    });

    return result;
  } catch (err) {
    // Transaction or network failure — return denied so callers degrade gracefully
    // instead of propagating an unhandled rejection.
    console.error('[planning-lock] acquirePlanningLock transaction failed:', err);
    return { granted: false, ownerId: null, ownerName: null, phase: null, expiresAt: null };
  } finally {
    acquireLockInFlight.delete(connectionId);
  }
}

// Per-connection release in-flight guards (same reasoning as acquire)
const releaseLockInFlight = new Map<string, boolean>();

export async function releasePlanningLock(connectionId: string, userId: string): Promise<void> {
  if (!connectionId || !userId) return;

  // Per-connection single-flight guard
  if (releaseLockInFlight.get(connectionId)) {
    console.warn('[planning-lock] release already in flight for connection', connectionId);
    return;
  }

  try {
    releaseLockInFlight.set(connectionId, true);

    const ref = doc(db, 'connections', connectionId);

    await runTransaction(db, async (transaction) => {
      const connSnap = await transaction.get(ref);
      
      if (!connSnap.exists()) {
        throw new Error('[planning-lock] connection not found');
      }

      const data = connSnap.data() || {};
      const currentOwner = data.planningOwnerId;

      // Only release if current user is the owner
      if (currentOwner === userId) {
        transaction.update(ref, {
          planningOwnerId: null,
          planningOwnerName: null,
          planningOwnerPhase: null,
          planningOwnerStartedAt: null,
          planningLockExpiresAt: null,
        });

        const logId = (s: string) => process.env.NODE_ENV === 'development' ? s : `${s.slice(0, 6)}…`;
        console.log('[planning-lock] release', { connectionId: logId(connectionId), userId: logId(userId) });
      } else {
        console.warn('[planning-lock] release attempted by non-owner', {
          connectionId,
          userId,
          currentOwner,
        });
      }
    });
  } catch (err) {
    console.error('[planning-lock] releasePlanningLock transaction failed:', err);
  } finally {
    releaseLockInFlight.delete(connectionId);
  }
}
